import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) nav("/dashboard");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(username, password);
        toast.success("Accesso effettuato");
        nav("/dashboard");
      } else {
        if (!dob) {
          setError("Data di nascita obbligatoria");
          setLoading(false);
          return;
        }
        const result = await register({ username, password, display_name: displayName || username, date_of_birth: dob });
        if (result && result.pending) {
          toast.success("Registrazione inviata · in attesa di approvazione dal Proprietario", { duration: 6000 });
          setMode("login");
          setPassword("");
          setDob("");
          setError("Il Proprietario deve approvarti prima di poter accedere. Riceverai conferma al più presto.");
        } else {
          toast.success("Registrazione completata");
          nav("/dashboard");
        }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-stretch">
      {/* Background image + overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/bg.webp')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#030614]/95 via-[#030614]/85 to-[#030614]/60" />

      <div className="relative z-10 w-full grid grid-cols-1 lg:grid-cols-2">
        {/* Left copy */}
        <div className="hidden lg:flex flex-col justify-between p-14">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Los Santos Customs" className="w-14 h-14 rounded-full" data-testid="login-logo" />
            <div className="ot-heading text-2xl font-black uppercase tracking-tight">
              <span className="text-blue-400">Los Santos</span><span className="text-[#FFE000]"> Customs</span>
            </div>
          </div>

          <div className="space-y-6 max-w-xl">
            <div className="ot-label text-blue-400/70">// Sistema Gestione Officina</div>
            <h1 className="ot-heading text-5xl xl:text-6xl font-black uppercase leading-[0.95] tracking-tighter">
              Timbra il tuo <br /><span className="text-blue-400">turno.</span> Costruisci la <br />leggenda.
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-md">
              Monitoraggio in tempo reale delle ore di servizio dei meccanici. Cronologia, classifica settimanale e fatturazione integrata.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
              {[
                { k: "24/7", l: "Servizio" },
                { k: "6", l: "Ranghi" },
                { k: "∞", l: "Turni" },
              ].map((s) => (
                <div key={s.l} className="ot-panel px-4 py-3">
                  <div className="ot-mono text-2xl text-blue-400 font-bold">{s.k}</div>
                  <div className="ot-label mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">LOSSANTOSCUSTOM.SYS · v1.0.0 · SECURE CHANNEL</div>
        </div>

        {/* Right form */}
        <div className="flex items-center justify-center p-6 lg:p-14">
          <div className="w-full max-w-md ot-panel-elev p-8 bg-[#060a1c]/80 ot-fade-up">
            <img src="/logo.png" alt="Los Santos Customs" className="w-20 h-20 rounded-full mx-auto mb-4 lg:hidden" data-testid="login-logo-mobile" />
            <div className="mb-6">
              <div className="ot-label mb-2">// {mode === "login" ? "Accesso operatore" : "Nuovo operatore"}</div>
              <h2 className="ot-heading text-3xl font-black uppercase text-slate-100">
                {mode === "login" ? "Entra in servizio" : "Registrati"}
              </h2>
              <div className="mt-2 text-sm text-slate-400">
                Usa le tue credenziali per accedere al pannello.
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="ot-label block mb-1.5">{mode === "register" ? "Nome e Cognome" : "Nome utente"}</label>
                <input
                  data-testid="login-username"
                  className="ot-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  placeholder={mode === "register" ? "Es. Mario Rossi" : ""}
                  required
                />
              </div>

              {mode === "register" && (
                <>
                  <div>
                    <label className="ot-label block mb-1.5">Data di nascita</label>
                    <input
                      data-testid="register-dob"
                      className="ot-input ot-mono"
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-[0.7rem] text-slate-500 -mt-2">
                    Il ruolo verrà assegnato dal Direttore dopo la registrazione.
                  </p>
                </>
              )}

              <div>
                <label className="ot-label block mb-1.5">Password</label>
                <input
                  data-testid="login-password"
                  className="ot-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                />
              </div>

              {error && (
                <div data-testid="login-error" className="text-red-400 text-sm border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="ot-btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? "Attendi..." : (mode === "login" ? "Accedi" : "Registrati")}
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="w-full text-center text-xs text-slate-400 hover:text-blue-400 transition-colors mt-2"
                data-testid="toggle-mode"
              >
                {mode === "login" ? "Nuovo operatore? Registrati" : "Hai già un account? Accedi"}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-white/5 text-[0.7rem] text-slate-500 ot-mono tracking-widest">
              CHANNEL ENCRYPTED · JWT_HS256
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
